import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

/* The UI kit's first real component, and the reason the kit exists.
 *
 * Before this, every button was inline styles repeated per screen. That is not a tidiness
 * complaint: a disabled button styled ad hoc is one where `opacity: 0.45` and `cursor: not-allowed`
 * get copied and the `disabled` attribute does not, producing a control that looks unavailable and
 * is still clickable — and on the review screen that control writes to an audit chain.
 *
 * Reached by path (`@/shared/ui/Button`) rather than through a barrel, per the boundary rules: the
 * kit is meant to be imported piecemeal so a screen using one component does not pull in twelve.
 */

export type ButtonTone = 'primary' | 'secondary' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly tone?: ButtonTone;
  /* Renders a spinner and disables the control. Separate from `disabled` because the two mean
   * different things to a user — "not allowed" versus "already happening" — and collapsing them
   * makes a slow click look like a rejected one, which is how double-submits happen. */
  readonly busy?: boolean;
  readonly children: ReactNode;
  /* `compact` is the chrome size — a theme toggle, a header control. A named size rather than a
   * caller's `style={{ minHeight: '28px' }}`, because an inline style beats the stylesheet and so
   * also defeats the coarse-pointer rule: the one control in the header would stay a 28px touch
   * target on a tablet while every other button grew to 44. */
  readonly size?: 'default' | 'compact' | 'block';
}

/* A class suffix per tone rather than a colour triple.
 *
 * The colours moved to `.lq-btn--*` in `system.css`, which is what let the secondary tone's border
 * be corrected: it was `--border-subtle` at 1.49:1, and WCAG 1.4.11 asks 3:1 for the *boundary* of
 * a control — legible as a card edge, invisible as a button. That fix is one line in CSS and would
 * have been three inline objects here.
 *
 * `satisfies Record<ButtonTone, string>` so a fourth tone is a compile error rather than a button
 * that renders with no class and inherits whatever the page happens to give it. */
const TONE_CLASS = {
  primary: 'lq-btn--primary',
  secondary: 'lq-btn--secondary',
  danger: 'lq-btn--danger',
} satisfies Record<ButtonTone, string>;

const SIZE_CLASS = {
  default: '',
  compact: 'lq-btn--compact',
  /* Spans its container. For a card whose only purpose is to be submitted — the auth screens — where
   * an inline-width primary action reads as secondary. */
  block: 'lq-btn--block',
} satisfies Record<NonNullable<ButtonProps['size']>, string>;

export function Button({
  tone = 'secondary',
  size = 'default',
  busy = false,
  disabled = false,
  children,
  style,
  /* Pulled out of `...rest` deliberately. **`{...rest}` is spread *after* `className`, so a caller's
   * class replaced the whole computed value** — `lq-btn`, the tone and the size all gone, leaving an
   * unstyled native button. Latent until the first caller passed one, which is a bug that ships
   * looking like a CSS problem: the class the caller asked for is present and every rule that makes
   * it a button is missing.
   *
   * Merged rather than refused, because a caller genuinely needs to *place* a button — the source
   * card pushes its destructive control to the far end — and placement is the caller's business
   * where appearance is not. */
  className,
  ...rest
}: ButtonProps): ReactElement {
  /* One derived flag, used for both the attribute and the styling. Deriving them separately is how
   * they drift — and `:disabled` in CSS now reads the attribute itself, so the appearance cannot
   * disagree with whether the control is actually clickable. That was the original bug this
   * component was written to prevent, and it is now structural rather than remembered. */
  const inert = disabled || busy;

  return (
    <button
      // An explicit type, because a button inside a form defaults to `submit` and a "Verify"
      // control that submits the registration form is a genuinely confusing bug.
      type={rest.type ?? 'button'}
      disabled={inert}
      aria-busy={busy || undefined}
      className={`lq-btn ${TONE_CLASS[tone]} ${SIZE_CLASS[size]} ${className ?? ''}`
        .replace(/\s+/g, ' ')
        .trim()}
      style={style}
      {...rest}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

function Spinner(): ReactElement {
  return (
    <span
      // Decorative: the state is already announced by aria-busy on the button, and a second
      // announcement would make a screen reader say "busy" twice.
      aria-hidden="true"
      className="lq-btn__spinner"
    />
  );
}
